import { useState, useRef, useMemo, useEffect } from 'react';
import { Box, Checkbox, Flex, Input, InputGroup, InputLeftAddon } from '@chakra-ui/react';
import { Controller, useFormState, useWatch } from 'react-hook-form';

import { TrashIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import { hasActiveSession } from 'Helper/checkIsSessionActive';
import { TextBold, TextRegular, TextSemiBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputSelect from 'UiKit/input-select';
import InputComponent from 'UiKit/input';
import InputUploadFile from 'UiKit/input-upload-file';
import InputTextAreaComponent from 'UiKit/input-textarea';

import RequiredProductsFields from 'View/brief-live/component/required-products-fields';
import SessionFields from 'View/brief-live/component/session-fields';

import styles from './styles.module.css';
import { isEqual } from 'lodash';

const GiveawayItem = ({
  t,
  index,
  control,
  register,
  setValue,
  errors,
  watchedGiveaways,
  watchedBrand,
  optionsGiveawayType,
  uploadImage,
  remove,
  onSave,
  onCancel,
  isSavePending,
  onUploadSuccess,
  trigger,
  defaultValues,
}) => {
  const [uploadingCount, setUploadingCount] = useState(0);
  const [isAllocationInvalid, setIsAllocationInvalid] = useState(false);
  const sessionFieldsRef = useRef(null);
  const incrementUploading = () => setUploadingCount((c) => c + 1);
  const decrementUploading = () => setUploadingCount((c) => c - 1);

  const watchGiveaway = useWatch({ control, name: `giveaways.${index}` });
  const watchedTotalStock = useWatch({ control, name: `giveaways.${index}.totalStock` });
  const watchedSessions = useWatch({ control, name: `giveaways.${index}.sessions` });
  const watchedRules = useWatch({ control, name: `giveaways.${index}.rules` });
  const watchedRulesType = useWatch({ control, name: `giveaways.${index}.rules.type` });
  const watchedWithProductPurchase = useWatch({ control, name: `giveaways.${index}.rules.withProductPurchase` });
  const watchedRequiredProducts = useWatch({ control, name: `giveaways.${index}.rules.requiredProducts` });
  const watchedProductName = useWatch({ control, name: `giveaways.${index}.productName` });
  const { dirtyFields } = useFormState({ control });
  const isFormDirty = !!dirtyFields?.giveaways?.[index] || !isEqual(defaultValues?.giveaways?.[index], watchGiveaway);

  useEffect(() => {
    sessionFieldsRef.current?.validateAllSessions();
  }, [watchedTotalStock, watchedSessions]);

  useEffect(() => {
    if (!watchedWithProductPurchase && watchedRulesType?.value === 'MINIMUM_PURCHASE') {
      setValue(`giveaways.${index}.rules.requiredProducts`, []);
    }
  }, [watchedWithProductPurchase, watchedRulesType, index, setValue]);

  const handleClickSave = async () => {
    if (trigger) {
      const isValid = await trigger();
      if (!isValid) return;
    }
    onSave?.();
  };

  const isRequirementDisabled = hasActiveSession(watchedGiveaways[index]?.sessions);

  const isProductsRequiredDisabled = !!onSave && isRequirementDisabled;

  const isFormInvalid = useMemo(() => {
    if (isAllocationInvalid) return true;

    const hasRequiredError = !watchedProductName || !watchedTotalStock;
    if (hasRequiredError) return true;

    const hasSessionError = watchedSessions?.some?.(
      (session) => !session?.accountLivestreamId || !session?.allocatedStock || !session?.promotionDate || !session?.startTime || !session?.endTime,
    );
    if (hasSessionError) return true;

    if (!watchedRulesType?.value) return true;

    let hasRequiredProductsError = false;
    if (watchedRulesType?.value && watchedRulesType.value !== 'MINIMUM_PURCHASE') {
      hasRequiredProductsError = watchedRequiredProducts?.some?.((item) => !item.productName);
    } else {
      hasRequiredProductsError = !watchedRules?.minimumPurchaseAmount;
      if (watchedWithProductPurchase) {
        hasRequiredProductsError = !watchedRules?.minimumPurchaseAmount || watchedRequiredProducts?.some?.((item) => !item.productName);
      }
    }
    return hasRequiredProductsError;
  }, [
    watchedProductName,
    watchedTotalStock,
    watchedSessions,
    watchedRules,
    watchedRulesType,
    watchedWithProductPurchase,
    watchedRequiredProducts,
    isAllocationInvalid,
  ]);

  return (
    <Box className={styles['giveaway-item__container']}>
      <Box className={styles['giveaway-item__box--head']}>
        <TextBold className={styles['giveaway-item__text--head-title']}>{t('formGiveaway.prizeInformation')}</TextBold>
        <Flex gap="8px" alignItems="center">
          <Condition statement={!isRequirementDisabled}>
            <ButtonOutline
              borderWidth="1.5px"
              color="var(--color-error50)"
              padding="6px"
              width="28px"
              height="28px"
              onClick={() => remove(index)}
              borderRadius={'2px'}
            >
              <TrashIcon color="var(--color-error50)" size="11px" />
            </ButtonOutline>
          </Condition>
          <Condition statement={onSave}>
            <ButtonOutline height="28px" onClick={onCancel} borderRadius={'2px'}>
              <TextSemiBold className={styles['giveaway-item__text--btn-cancel']}>{t('formGiveaway.cancelButton')}</TextSemiBold>
            </ButtonOutline>
            <ButtonSolid
              height="28px"
              isLoading={isSavePending}
              disabled={uploadingCount > 0 || isFormInvalid || !isFormDirty}
              onClick={handleClickSave}
              borderRadius={'2px'}
            >
              <TextSemiBold className={styles['giveaway-item__text--btn-save']}>{t('formGiveaway.saveButton')}</TextSemiBold>
            </ButtonSolid>
          </Condition>
        </Flex>
      </Box>
      <Box className={styles['giveaway-item__box--gift-info']}>
        <Controller
          name={`giveaways.${index}.imagePath`}
          control={control}
          render={({ field }) => {
            return (
              <InputUploadFile
                onChange={async (file) => {
                  field.onChange('');
                  incrementUploading();
                  try {
                    const result = await uploadImage({ file });
                    field.onChange(result);
                    onUploadSuccess?.();
                  } finally {
                    decrementUploading();
                  }
                }}
                initialPreview={field.value?.imagePath || null}
                height="70px"
                width="72px"
              />
            );
          }}
        />
        <Box className={styles['giveaway-item__box--inputs']}>
          <InputComponent
            label={t('formGiveaway.productName')}
            placeholder={t('formGiveaway.enterName')}
            isRequired
            className={styles['giveaway-item__input--product-name']}
            value={watchedGiveaways[index]?.productName ?? ''}
            register={{ ...register(`giveaways.${index}.productName`, { required: 'Nama Produk wajib diisi' }) }}
            error={errors.giveaways?.[index]?.productName}
          />
          <InputComponent
            label={t('formGiveaway.totalStock')}
            placeholder={t('formGiveaway.totalStock')}
            className={styles['giveaway-item__input--total-stok']}
            isRequired
            value={watchedGiveaways[index]?.totalStock ?? ''}
            register={{
              ...register(`giveaways.${index}.totalStock`, {
                required: 'Total Stok wajib diisi',
                onChange: (event) => {
                  let cleanedValue = event.target.value.replace(/[^0-9]/g, '').replace('-', '');
                  if (cleanedValue[0] === '0') {
                    cleanedValue = cleanedValue.slice(1);
                  }
                  setValue(`giveaways.${index}.totalStock`, cleanedValue);
                },
                min: { value: 1, message: 'Total Stok harus angka positif' },
                onBlur: () => {
                  sessionFieldsRef.current?.validateAllSessions();
                },
              }),
            }}
            error={errors.giveaways?.[index]?.totalStock}
          />
        </Box>
      </Box>
      <Flex width={'100%'}>
        <Box className={styles['giveaway-item__box--requirement']}>
          <TextBold className={styles['giveaway-item__text--head-title']}>{t('formGiveaway.requirement')}</TextBold>
          <Box className={styles['giveaway-item__box--requirement-fields']}>
            <Controller
              name={`giveaways.${index}.rules.type`}
              control={control}
              rules={{ required: 'Tipe Giveaway wajib dipilih' }}
              render={({ field }) => (
                <InputSelect
                  label={t('formGiveaway.giveawayType')}
                  value={field.value}
                  onChange={(props) => {
                    field.onChange(props);
                    setValue(`giveaways.${index}.rules.minimumPurchaseAmount`, null);
                    setValue(`giveaways.${index}.rules.withProductPurchase`, false);
                    setValue(`giveaways.${index}.rules.requiredProducts`, []);
                  }}
                  isRequired
                  options={optionsGiveawayType || []}
                  error={errors.giveaways?.[index]?.rules?.type}
                />
              )}
            />
            <Controller
              name={`giveaways.${index}.rules.note`}
              control={control}
              render={({ field }) => (
                <InputTextAreaComponent
                  maxLength={'100'}
                  placeholder={t('formGiveaway.notesOptional')}
                  field={field}
                  value={field.value}
                  register={{ ...register(`giveaways.${index}.rules.note`) }}
                />
              )}
            />
            <Condition statement={watchedGiveaways[index]?.rules?.type?.value}>
              <Condition
                statement={watchedGiveaways[index]?.rules?.type?.value === 'MINIMUM_PURCHASE'}
                fallback={
                  <RequiredProductsFields
                    t={t}
                    control={control}
                    register={register}
                    setValue={setValue}
                    giveawayIndex={index}
                    watchedGiveaways={watchedGiveaways}
                    watchedBrand={watchedBrand}
                    isEditing={isProductsRequiredDisabled}
                  />
                }
              >
                <Box className={styles['giveaway-item__box--minimum-purchase']}>
                  <Box>
                    <TextRegular className={styles['giveaway-item__text--minimum-purchase-label']}>{t('formGiveaway.promotionMinimumSpend')}</TextRegular>
                    <InputGroup width={'70%'}>
                      <InputLeftAddon className={styles['giveaway-item__input-addon--rp']}>Rp</InputLeftAddon>
                      <Input
                        className={styles['giveaway-item__input--minimum-purchase']}
                        placeholder="0"
                        {...register(`giveaways.${index}.rules.minimumPurchaseAmount`, {
                          onChange: (e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            e.target.value = raw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                          },
                        })}
                      />
                    </InputGroup>
                  </Box>
                  <Checkbox
                    marginTop="20px"
                    {...register(`giveaways.${index}.rules.withProductPurchase`)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setValue(`giveaways.${index}.rules.withProductPurchase`, checked);
                      setValue(`giveaways.${index}.rules.requiredProducts`, []);
                    }}
                  >
                    <TextRegular className={styles['giveaway-item__text--checkbox-label']}>{t('formGiveaway.withProductPurchase')}</TextRegular>
                  </Checkbox>
                  <Condition statement={watchedGiveaways[index]?.rules?.withProductPurchase}>
                    <RequiredProductsFields
                      t={t}
                      control={control}
                      setValue={setValue}
                      giveawayIndex={index}
                      watchedGiveaways={watchedGiveaways}
                      watchedBrand={watchedBrand}
                    />
                  </Condition>
                </Box>
              </Condition>
            </Condition>
          </Box>
        </Box>
        <Box className={styles['giveaway-item__box--duration']}>
          <TextBold className={styles['giveaway-item__text--head-title']}>{t('formGiveaway.giveawayDuration')}</TextBold>
          <SessionFields
            ref={sessionFieldsRef}
            t={t}
            giveawayIndex={index}
            watchedGiveaways={watchedGiveaways}
            watchedBrand={watchedBrand}
            isEditing={!!onSave}
            onAllocationValidationChange={(isInvalid) => setIsAllocationInvalid(isInvalid)}
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default GiveawayItem;
